// external
import React, { FC, useState } from 'react';
import { Box, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';

// internal
import useService from '../../../hooks/service.hook';
import useSnackbarHelper from '../../../hooks/snackbar.hook';
import EquipmentSharingService from '../../../services/equipment-sharing.service';
import EquipmentService from '../../../services/equipments.service';
import { UserAvatar } from '../../user-avatar/user-avatar.component';

export interface EquipmentManagerRow {
  id: number;
  firstname?: string | null;
  lastname?: string | null;
  profile_image?: string | null;
  hidden: boolean;
}

interface EquipmentManagersTabProps {
  equipmentId?: number;
  managers: EquipmentManagerRow[] | undefined;
  onEquipmentUpdate: (equipment: any) => void;
}

const EquipmentManagersTab: FC<EquipmentManagersTabProps> = ({ equipmentId, managers, onEquipmentUpdate }) => {
  const equipmentSharingService = useService(EquipmentSharingService);
  const equipmentService = useService(EquipmentService);
  const { showSnackbar } = useSnackbarHelper();
  const [pendingManagerId, setPendingManagerId] = useState<number | null>(null);

  if (!equipmentId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Save this equipment first to manage visibility for managers.
      </Typography>
    );
  }

  const list = Array.isArray(managers) ? managers : [];

  if (list.length === 0) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', py: 5, px: 2 }}
      >
        <SupervisorAccountIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
        <Typography sx={{ mt: 1.5, textAlign: 'center', maxWidth: 360, fontSize: 13, color: 'text.secondary' }}>
          No equipment managers yet. Invite someone from the options menu (⋮) or via the Equipment page.
        </Typography>
      </Box>
    );
  }

  const refreshEquipment = () => {
    equipmentService
      .getEquipment(equipmentId)
      .then((res) => onEquipmentUpdate(res.data.data))
      .catch(() => {
        showSnackbar('Could not refresh equipment.', 'error');
      });
  };

  const handleHiddenChange = (managerId: number, nextHidden: boolean) => {
    setPendingManagerId(managerId);
    const req = nextHidden
      ? equipmentSharingService.hideEquipmentFromManager(equipmentId, managerId)
      : equipmentSharingService.unhideEquipmentForManager(equipmentId, managerId);
    req
      .then(() => refreshEquipment())
      .catch((err) => {
        let msg = 'Could not update visibility. Please try again.';
        if (err?.response?.status === 403) msg = 'You do not have permission to change this.';
        if (err?.response?.status === 400) msg = err?.response?.data?.message || msg;
        showSnackbar(msg, 'error');
      })
      .finally(() => setPendingManagerId(null));
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          mb: 1,
        }}
      >
        Managers
      </Typography>
      <Box
        sx={{
          border: `1px solid`,
          borderColor: 'divider',
          borderRadius: '12px',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        {list.map((m, idx) => {
          const name = `${m.firstname ?? ''} ${m.lastname ?? ''}`.trim() || `Manager #${m.id}`;
          const busy = pendingManagerId === m.id;

          return (
            <Box
              key={m.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderBottom: idx < list.length - 1 ? `1px solid` : 'none',
                borderColor: 'divider',
              }}
            >
              {/* Avatar */}
              <UserAvatar
                src={m.profile_image}
                firstname={m.firstname}
                lastname={m.lastname}
                userId={m.id}
                size={36}
                fontSize="0.8rem"
              />

              {/* Name */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                  {name}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
                  {m.hidden ? 'Hidden from this manager' : 'Visible to this manager'}
                </Typography>
              </Box>

              {/* Hidden toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {busy ? (
                  <CircularProgress size={20} />
                ) : (
                  <FormControlLabel
                    sx={{ mr: 0, ml: 0, gap: 0.5 }}
                    control={
                      <Switch
                        size="small"
                        checked={m.hidden}
                        onChange={(_, checked) => handleHiddenChange(m.id, checked)}
                        inputProps={{ 'aria-label': `Hidden from ${name}` }}
                      />
                    }
                    label={<Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Hidden</Typography>}
                    labelPlacement="start"
                  />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default EquipmentManagersTab;
